import {
  Button,
  ButtonGroup,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React from 'react';

import { legacyOperationsCheckDetailsRoute } from 'src/app/graph/routes';
import { absoluteURL } from 'src/lib/absoluteURL';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  CheckWorkflowStatus,
  CheckWorkflowTaskStatus,
} from 'src/lib/graphqlTypes/types';

/**
 * This modal prompts the user for confirmation when the build or operation check
 * fail but they would still like to proceed with updating the contract configuration.
 */
export const ChecksConfirmationModal = ({
  checkWorkflow,
  isOpen,
  onApproval,
  onClose,
}: {
  checkWorkflow: GraphQLTypes.CheckWorkflowStatusQuery_service_checkWorkflow;
  isOpen: boolean;
  onApproval: () => void;
  onClose: () => void;
}) => {
  const numOfBuildErrors = checkWorkflow.buildTask
    ? checkWorkflow.buildTask.__typename === 'FilterCheckTask' &&
      checkWorkflow.buildTask.buildResult?.__typename ===
        'FilterBuildCheckFailed' &&
      checkWorkflow.buildTask.buildResult.errors.length
    : 0;
  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Are you sure you want to update?</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {checkWorkflow.status === CheckWorkflowStatus.PENDING ? (
            <div>
              Schema checks are running in the background. Updating now may
              result in breaking changes.
            </div>
          ) : checkWorkflow.status === CheckWorkflowStatus.PASSED ? (
            <div>
              Schema checks have completed and found no breaking changes. Submit
              or go back to review your changes.
            </div>
          ) : checkWorkflow.buildTask?.status ===
            CheckWorkflowTaskStatus.FAILED ? (
            <div>
              Your filter changes resulted in{' '}
              <span className="font-semibold text-red">
                {numOfBuildErrors} build errors
              </span>{' '}
              that are preventing your contract from being built. We recommend
              you go back and update your contract configuration or your source
              variant’s (
              <span className="text-link">
                {checkWorkflow.baseVariant?.sourceVariant?.id}
              </span>
              ) schema.
            </div>
          ) : checkWorkflow.operationsTask?.status ===
            CheckWorkflowTaskStatus.FAILED ? (
            <div>
              Your changes to <b>{checkWorkflow.baseVariant?.id}</b> will affect{' '}
              <span className="font-semibold text-red">
                {
                  checkWorkflow.operationsTask.result
                    ?.numberOfAffectedOperations
                }
              </span>{' '}
              out of{' '}
              <span className="font-semibold">
                {checkWorkflow.operationsTask.result?.numberOfCheckedOperations}
              </span>{' '}
              checked operations.{' '}
              <Link
                to={
                  checkWorkflow.operationsTask
                    ? absoluteURL(
                        legacyOperationsCheckDetailsRoute.path({
                          graphId: checkWorkflow.baseVariant?.graphId as string,
                          graphVariant: checkWorkflow.baseVariant
                            ?.name as string,
                          taskId: checkWorkflow.operationsTask.id,
                        }),
                      )
                    : ''
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                View details
              </Link>
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              Go back
            </Button>
            <Button variant="primary" onClick={onApproval}>
              Yes, update now
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
